// /src/pages/CampaignsReportPage.js
// VERSIÓN CORREGIDA: Se ajusta el orden de las columnas fijas a ID, Fuente de Tráfico, Campaña.

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
// En tu proyecto real, asegúrate de que esta ruta sea correcta.
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Stack,
    CircularProgress, Alert, Tooltip, IconButton, Breadcrumbs, Link as MuiLink,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Card, CardContent,
    Chip, Skeleton, Divider, ListSubheader, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, DialogActions,
    Switch, ListItemIcon, Menu, Snackbar, Checkbox, Grid
} from '@mui/material';
import { useTheme, alpha, Theme } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SearchIcon from '@mui/icons-material/Search';
import InsightsIcon from '@mui/icons-material/Insights';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// --- TIPOS E INTERFACES ---
type ReportRow = Record<string, any>;
type DrillDownPath = { dimension: string; value: string | number; label: string };
type AdvancedFilter = {
    id: number;
    column: string;
    operator: string;
    value: string;
};

// --- CONFIGURACIÓN DEL COMPONENTE ---
const allDimensions = [
    { id: 'campaign', label: 'Campaña', group: 'Campaña' },
    { id: 'offer', label: 'Oferta', group: 'Campaña' },
    { id: 'traffic_source', label: 'Fuente de Tráfico', group: 'Campaña' },
    { id: 'landing_page', label: 'Landing Page', group: 'Campaña' },
    { id: 'country_code', label: 'País', group: 'Geografía' },
    { id: 'rule', label: 'Regla', group: 'Flujo de Tráfico' },
    { id: 'path', label: 'Ruta', group: 'Flujo de Tráfico' },
    { id: 'region_name', label: 'Región/Estado', group: 'Geografía' },
    { id: 'city_name', label: 'Ciudad', group: 'Geografía' },
    { id: 'isp', label: 'ISP', group: 'Geografía' },
    { id: 'asn', label: 'Red (ASN)', group: 'Geografía' },
    { id: 'ip_address', label: 'Dirección IP', group: 'Geografía' },
    { id: 'device_type', label: 'Tipo de Dispositivo', group: 'Dispositivo' },
    { id: 'browser_name', label: 'Navegador', group: 'Dispositivo' },
    { id: 'browser_version', label: 'Versión del Navegador', group: 'Dispositivo' },
    { id: 'os_name', label: 'Sistema Operativo', group: 'Dispositivo' },
    { id: 'os_version', label: 'Versión del SO', group: 'Dispositivo' },
    { id: 'device_vendor', label: 'Fabricante Dispositivo', group: 'Dispositivo' },
    { id: 'device_model', label: 'Modelo Dispositivo', group: 'Dispositivo' },
    { id: 'referrer', label: 'Referrer', group: 'Contexto' },
    { id: 'language', label: 'Idioma', group: 'Contexto' },
    { id: 'sub_id1', label: 'SubId1', group: 'SubIDs' }, { id: 'sub_id2', label: 'SubId2', group: 'SubIDs' },
    { id: 'sub_id3', label: 'SubId3', group: 'SubIDs' }, { id: 'sub_id4', label: 'SubId4', group: 'SubIDs' },
    { id: 'sub_id5', label: 'SubId5', group: 'SubIDs' }, { id: 'sub_id6', label: 'SubId6', group: 'SubIDs' },
    { id: 'sub_id7', label: 'SubId7', group: 'SubIDs' }, { id: 'sub_id8', label: 'SubId8', group: 'SubIDs' },
    { id: 'sub_id9', label: 'SubId9', group: 'SubIDs' }, { id: 'sub_id10', label: 'SubId10', group: 'SubIDs' },
    { id: 'date', label: 'Fecha', group: 'Tiempo' },
    { id: 'hour', label: 'Hora', group: 'Tiempo' },
];

const dimensionMap: Record<string, { alias: string, idKey: string }> = allDimensions.reduce((acc, dim) => {
    const isNameBased = ['campaign', 'offer', 'traffic_source', 'landing_page'].includes(dim.id);
    acc[dim.id] = {
        alias: isNameBased ? `${dim.id}_name` : dim.id,
        idKey: isNameBased ? `${dim.id}_id` : `${dim.id}`
    };
    return acc;
}, {} as Record<string, { alias: string, idKey: string }>);

// --- NOMBRES DE COLUMNAS DE MÉTRICAS ACTUALIZADOS ---
const allMetricCards = [
    { key: 'total_clicks', label: 'Clicks' },
    { key: 'lp_clicks', label: 'LP Clicks' },
    { key: 'lp_ctr', label: 'LP CTR' },
    { key: 'total_conversions', label: 'Conversiones' },
    { key: 'cvr', label: 'CR%' },
    { key: 'total_cost', label: 'Costo' },
    { key: 'total_payout', label: 'Payout' },
    { key: 'profit', label: 'Beneficio' },
    { key: 'roi', label: 'ROI' },
    { key: 'cpc', label: 'CPC' },
    { key: 'epc', label: 'EPC' },
    { key: 'ecpa', label: 'eCPA' }, // Agregado eCPA
];

const ROWS_PER_PAGE = 25;
const datePresetTranslations: { [key: string]: string } = { 'today': 'Hoy', 'yesterday': 'Ayer', 'last_7_days': 'Últimos 7 Días', 'last_30_days': 'Últimos 30 Días', 'this_month': 'Este Mes', 'last_month': 'Mes Pasado', 'all_time': 'Histórico' };
const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);
const formatDateCell = (value: any) => value && typeof value === 'string' ? value.split('T')[0] : String(value || '');

const separators = [
    { key: 'newline', label: 'Salto de Línea', value: '\n' },
    { key: 'comma', label: 'Coma', value: ',' },
    { key: 'space', label: 'Espacio', value: ' ' },
    { key: 'tab', label: 'Tabulador', value: '\t' },
    { key: 'semicolon', label: 'Punto y Coma', value: ';' },
];

const filterOperators = [
    { value: '>=', label: '>=' },
    { value: '>', label: '>' },
    { value: '==', label: '==' },
    { value: '<=', label: '<=' },
    { value: '<', label: '<' },
    { value: '!=', label: '!=' },
];

const getMetricStyle = (header: string, value: any, theme: Theme) => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return {};

    const originalMetricKey = allMetricCards.find(m => m.label === header)?.key || header;

    if (originalMetricKey === 'profit' || originalMetricKey === 'roi') {
        if (numericValue > 0) return { color: theme.palette.success.dark, fontWeight: '500' };
        if (numericValue < 0) return { color: theme.palette.error.dark, fontWeight: '500' };
    }
    return {};
};

const formatMetricCell = (header: string, value: any) => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
        return 'N/A';
    }

    const originalMetricKey = allMetricCards.find(m => m.label === header)?.key || header;

    if (['epc', 'cpc', 'ecpa'].includes(originalMetricKey)) { // Agregado 'ecpa' aquí
        return <Chip label={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 5 }).format(numericValue)} size="small" variant="outlined" />;
    }
    if (['total_cost', 'total_payout', 'profit'].includes(originalMetricKey)) {
        return <Chip label={formatCurrency(numericValue)} size="small" variant="outlined" />;
    }
    if (['roi', 'lp_ctr', 'cvr'].includes(originalMetricKey)) {
        const color = (originalMetricKey === 'roi' && numericValue < 0) ? 'error' : (originalMetricKey === 'roi' && numericValue > 0 ? 'success' : 'default');
        return <Chip label={`${numericValue.toFixed(2)}%`} size="small" color={color} variant={color === 'default' ? 'outlined' : 'filled'} />;
    }
    return formatNumber(numericValue);
};

const formatDimensionCell = (row: ReportRow, header: string) => {
    const dimensionEntry = Object.entries(dimensionMap).find(([key, val]) => val.alias === header || val.idKey === header || key === header);

    if (dimensionEntry) {
        const [dimKey, dimInfo] = dimensionEntry;

        const idKeysToFormatDirectly = ['campaign_id', 'offer_id', 'traffic_source_id', 'landing_page_id'];
        if (idKeysToFormatDirectly.includes(header)) {
            const idValue = row[header];
            return String(idValue === null || idValue === undefined ? 'N/A' : idValue);
        }

        const nameAliasesToFormatDirectly = [
            dimensionMap['campaign']?.alias,
            dimensionMap['traffic_source']?.alias,
            dimensionMap['offer']?.alias,
            dimensionMap['landing_page']?.alias
        ].filter(Boolean);

        if (nameAliasesToFormatDirectly.includes(header)) {
            const nameValue = row[header];
            if (header === dimensionMap['landing_page']?.alias && (nameValue === null || nameValue === undefined)) {
                return 'Directo a Oferta';
            }
            return String(nameValue === null || nameValue === undefined ? 'N/A' : nameValue);
        }
    }

    const value = row[header];
    if (header === 'language' && typeof value === 'string') return value.split(',')[0];
    if (header === 'date') return formatDateCell(value);

    return String(value === null || value === undefined ? 'N/A' : value);
};

// --- Helper para mapear nombres de display a claves de backend ---
// Moved outside the component to be globally accessible
const getBackendKeyFromDisplayName = (displayName: string, reportData: ReportRow[]): string | undefined => {
    const metric = allMetricCards.find(m => m.label === displayName);
    if (metric) return metric.key;

    for (const dim of allDimensions) {
        if (dim.label === displayName) {
            return dim.id;
        }
        const dimInfo = dimensionMap[dim.id];
        if (dimInfo) {
            if (dimInfo.alias === displayName) return dimInfo.alias;
            if (dimInfo.idKey === displayName) return dimInfo.idKey;
        }
    }

    if (displayName === 'ID') return 'campaign_id';
    if (displayName === 'Nombre') return 'campaign_name';
    if (displayName === 'Traffic Source') return 'traffic_source_name';
    if (displayName === 'Oferta ID') return 'offer_id';
    if (displayName === 'LP ID') return 'landing_page_id';

    const inferredBackendKey = displayName.toLowerCase().replace(/ /g, '_');
    if (reportData.length > 0 && Object.keys(reportData[0]).includes(inferredBackendKey)) {
        return inferredBackendKey;
    }

    return undefined;
};

// --- Helper para mapear claves de backend a nombres de display (inverso) ---
// Moved outside the component to be globally accessible
const getDisplayNameFromBackendKey = (backendKey: string): string => {
    const displayNamesMap: Record<string, string> = {
        'campaign_id': 'ID',
        'campaign_name': 'Nombre',
        'traffic_source_name': 'Traffic Source',
        'offer_id': 'Oferta ID',
        'landing_page_id': 'LP ID',
    };

    allDimensions.forEach(dim => {
        const dimInfo = dimensionMap[dim.id];
        if (dimInfo) {
            displayNamesMap[dim.id] = dim.label;
            displayNamesMap[dimInfo.alias] = dim.label;
            if (dimInfo.idKey && !(dimInfo.idKey in displayNamesMap)) {
                displayNamesMap[dimInfo.idKey] = `${dim.label} ID`;
            }
        } else {
            displayNamesMap[dim.id] = dim.label;
        }
    });
    allMetricCards.forEach(metric => {
        displayNamesMap[metric.key] = metric.label;
    });

    return displayNamesMap[backendKey] || backendKey.replace(/_/g, ' ').toUpperCase();
};


const CampaignsReportPage: React.FC = () => {
    // --- ESTADOS ---
    const [reportData, setReportData] = useState<ReportRow[]>([]);
    const [totals, setTotals] = useState<ReportRow | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    
    // CAMBIO AQUÍ: Inicializar startDate y endDate al día de hoy
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Asegurarse de que sea el inicio del día
    const todayFormatted = today.toISOString().split('T')[0];

    const [startDate, setStartDate] = useState<string>(todayFormatted);
    const [endDate, setEndDate] = useState<string>(todayFormatted);

    const [dimension, setDimension] = useState<string>('campaign');
    const [drillDownPath, setDrillDownPath] = useState<DrillDownPath[]>([]);
    const [activeDrilldownGrouping, setActiveDrilldownGrouping] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'ascending' | 'descending' } | null>({ key: 'profit', direction: 'descending' });
    const [currentPage, setCurrentPage] = useState(1);

    const [columnOrder, setColumnOrder] = useState<string[]>([]);
    const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});

    const [visibleCards, setVisibleCards] = useState<Record<string, boolean>>({});
    const [cardOrder, setCardOrder] = useState<string[]>([]);
    const [isCardManagerOpen, setIsCardManagerOpen] = useState(false);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedColumn, setSelectedColumn] = useState<null | string>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilter[]>([]);
    const theme = useTheme();

    // Estado para la selección de filas (campañas)
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
    const [resizingColumn, setResizingColumn] = useState<string | null>(null);
    const [startX, setStartX] = useState<number>(0);
    const [startWidth, setStartWidth] = useState<number>(0);

    const drilledDimensions = useMemo(() => drillDownPath.map(p => p.dimension), [drillDownPath]);
    const availableDrilldownDimensions = useMemo(() => allDimensions.filter(d => !drilledDimensions.includes(d.id)), [drilledDimensions]);

    // Modificamos fetchData para que ya no reciba campaignIdsToFilter, ahora todo se maneja con drillDownPath
    const fetchData = useCallback(async () => {
        const mainReportDimension = drillDownPath.length > 0 ? activeDrilldownGrouping : dimension;
        if (!mainReportDimension) { setIsLoading(false); setReportData([]); setTotals(null); return; }

        setIsLoading(true);
        setError(null);

        const apiEndDate = new Date(endDate);
        apiEndDate.setDate(apiEndDate.getDate() + 1);
        const formattedEndDate = apiEndDate.toISOString().split('T')[0];

        let filterString = '';
        
        // La lógica de filtro ahora reside COMPLETAMENTE en drillDownPath
        drillDownPath.forEach(pathItem => {
            const filterDimensionKey = pathItem.dimension; // This is the base dimension key (e.g., 'campaign', 'offer')
            filterString += `&${filterDimensionKey}=${encodeURIComponent(pathItem.value)}`;
        });

        // Determinar la dimensión que se pedirá al backend
        let dimensionsParam = mainReportDimension;
        // Si estamos en el nivel base de campañas y no hay drilldown (ni siquiera el agrupado),
        // añadir traffic_source para el reporte general de campañas.
        if (drillDownPath.length === 0 && mainReportDimension === 'campaign') {
            dimensionsParam += ',traffic_source';
        }
        // Si drillDownPath tiene una entrada agrupada, mainReportDimension ya será la siguiente agrupación (e.g., 'offer')
        // y filterString ya tendrá el `campaign=ID1,ID2`


        const mainReportUrl = `${API_BASE_URL}/reports/query?startDate=${startDate}&endDate=${formattedEndDate}&dimensions=${dimensionsParam}${filterString}`;

        let totalsDimensions = 'campaign';
        if (drillDownPath.length > 0) {
            totalsDimensions = drillDownPath[0].dimension; // Los totales se agrupan por el primer elemento del drilldown path
        }

        const totalsReportUrl = `${API_BASE_URL}/reports/query?startDate=${startDate}&endDate=${formattedEndDate}&dimensions=${totalsDimensions}${filterString}`;

        console.log("--- FETCH DATA DEBUG ---");
        console.log("Current DrillDown Path:", drillDownPath);
        console.log("Generated Filter String (Frontend):", filterString);
        console.log("Main Report URL:", mainReportUrl);
        console.log("Totals Report URL:", totalsReportUrl);
        console.log("--------------------------");


        try {
            const [mainResponse, totalsResponse] = await Promise.all([
                axios.get<ReportRow[]>(mainReportUrl),
                axios.get<ReportRow[]>(totalsReportUrl)
            ]);
            
            // Función para calcular eCPA y añadirlo a cada fila
            const processReportRows = (rows: ReportRow[]) => {
                return rows.map(row => {
                    const totalCost = parseFloat(row.total_cost) || 0;
                    const totalConversions = parseFloat(row.total_conversions) || 0;
                    const ecpa = totalConversions > 0 ? totalCost / totalConversions : 0;
                    return { ...row, ecpa: parseFloat(ecpa.toFixed(2)) }; // Formatear a 2 decimales
                });
            };

            setReportData(processReportRows(mainResponse.data));

            if (totalsResponse.data.length > 0) {
                const totalRow = totalsResponse.data.reduce((acc, row) => {
                    Object.keys(row).forEach(key => {
                        if (!key.endsWith('_id') && !key.endsWith('_name') && !isNaN(parseFloat(row[key]))) {
                            acc[key] = (acc[key] || 0) + parseFloat(row[key]);
                        }
                    });
                    return acc;
                }, {} as ReportRow);
                const totalProfit = (totalRow.total_payout || 0) - (totalRow.total_cost || 0);
                totalRow.profit = totalProfit;
                totalRow.roi = totalRow.total_cost > 0 ? (totalProfit / totalRow.total_cost) * 100 : 0;
                // Calcular CPC, EPC y eCPA para los totales
                totalRow.cpc = totalRow.total_clicks > 0 ? (totalRow.total_cost || 0) / totalRow.total_clicks : 0;
                totalRow.epc = totalRow.total_clicks > 0 ? (totalRow.total_payout || 0) / totalRow.total_clicks : 0;
                totalRow.ecpa = totalRow.total_conversions > 0 ? (totalRow.total_cost || 0) / totalRow.total_conversions : 0;
                setTotals(totalRow);
            } else {
                setTotals({ total_clicks: 0, total_cost: 0, total_payout: 0, profit: 0, roi: 0, lp_clicks: 0, lp_ctr: 0, total_conversions: 0, cpc: 0, epc: 0, ecpa: 0 }); // Agregado eCPA a los totales iniciales
            }
        } catch (err: any) {
            setError(err.response?.data?.message || "Error de red.");
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, dimension, drillDownPath, activeDrilldownGrouping]); // selectedRows YA NO ES UNA DEPENDENCIA

    // --- INICIO: LÓGICA DE AUTO-REFRESH PARA REPORTES DE "HOY" ---
    useEffect(() => {
        // Ejecutar la primera carga de datos.
        fetchData();

        // Determinar si el reporte es para "hoy" para activar el auto-refresh.
        const today = new Date();
        const todayFormatted = today.toISOString().split('T')[0];
        const isLiveReport = endDate >= todayFormatted;

        if (isLiveReport) {
            // Si es un reporte en vivo, establecer un intervalo de 10 segundos.
            const intervalId = setInterval(() => {
                console.log('Auto-refreshing live report...');
                fetchData();
            }, 30000); // 10000 ms = 10 segundos

            // Limpiar el intervalo cuando el componente se desmonte o las dependencias cambien.
            return () => clearInterval(intervalId);
        }
    // La dependencia de `fetchData` asegura que siempre usemos la versión más reciente de la función.
    // La dependencia de `endDate` asegura que el intervalo se cree/destruya si el rango de fechas cambia.
    }, [fetchData, endDate]);
    // --- FIN: LÓGICA DE AUTO-REFRESH ---

    const tableHeaders = useMemo(() => {
        if (reportData.length === 0) return [];
        const headers = new Set<string>();
        const currentGroupingDimension = drillDownPath.length > 0 ? activeDrilldownGrouping : dimension;

        const displayNames: Record<string, string> = {
            'campaign_id': 'ID',
            'campaign_name': 'Nombre',
            'traffic_source_name': 'Fuente de Tráfico',
            'offer_id': 'Oferta ID',
            'landing_page_id': 'LP ID',
        };

        allDimensions.forEach(dim => {
            if (dim.id in dimensionMap && dimensionMap[dim.id].alias) {
                displayNames[dimensionMap[dim.id].alias] = dim.label;
            } else {
                displayNames[dim.id] = dim.label;
            }
            if (dim.id in dimensionMap && dimensionMap[dim.id].idKey && !(dimensionMap[dim.id].idKey in displayNames)) {
                displayNames[dimensionMap[dim.id].idKey] = `${dim.label} ID`;
            }
        });
        allMetricCards.forEach(metric => {
            displayNames[metric.key] = metric.label;
        });


        if (currentGroupingDimension === 'campaign') {
            headers.add('campaign_id');
        }

        Object.keys(reportData[0]).forEach(key => {
            if (!key.endsWith('_id') || key === 'campaign_id' || key === 'offer_id' || key === 'traffic_source_id' || key === 'landing_page_id') {
                headers.add(key);
            }
        });

        let finalHeaders = Array.from(headers);

        const orderedHeaders: string[] = [];
        const dimensionColumns: string[] = [];
        const currentDimConfig = dimensionMap[currentGroupingDimension];

        if (currentGroupingDimension === 'campaign') {
            if (finalHeaders.includes('campaign_id')) dimensionColumns.push('campaign_id');
            if (currentDimConfig?.alias && finalHeaders.includes(currentDimConfig.alias)) dimensionColumns.push(currentDimConfig.alias);
            if (dimensionMap['traffic_source']?.alias && finalHeaders.includes(dimensionMap['traffic_source'].alias)) dimensionColumns.push(dimensionMap['traffic_source'].alias);
        } else if (currentDimConfig) {
            if (currentDimConfig.idKey && finalHeaders.includes(currentDimConfig.idKey)) {
                dimensionColumns.push(currentDimConfig.idKey);
            }
            if (currentDimConfig.alias && finalHeaders.includes(currentDimConfig.alias) && currentDimConfig.alias !== currentDimConfig.idKey) {
                dimensionColumns.push(currentDimConfig.alias);
            }
            if (!dimensionColumns.includes(currentGroupingDimension) && finalHeaders.includes(currentGroupingDimension)) {
                dimensionColumns.push(currentGroupingDimension);
            }
        }

        dimensionColumns.forEach(col => {
            if (finalHeaders.includes(col)) {
                orderedHeaders.push(col);
                finalHeaders = finalHeaders.filter(h => h !== col);
            }
        });

        orderedHeaders.push(...finalHeaders);

        return orderedHeaders.map(backendKey => getDisplayNameFromBackendKey(backendKey));

    }, [reportData, dimension, drillDownPath, activeDrilldownGrouping]);

    useEffect(() => {
        const savedOrder = localStorage.getItem('reportColumnOrder');
        const savedVisibility = localStorage.getItem('reportColumnVisibility');
        const savedWidths = localStorage.getItem('reportColumnWidths');

        const backendHeaders = reportData.length > 0 ? Object.keys(reportData[0]).filter(key =>
            !key.endsWith('_id') || ['campaign_id', 'offer_id', 'traffic_source_id', 'landing_page_id'].includes(key)
        ) : [];

        const currentGroupingDimension = drillDownPath.length > 0 ? activeDrilldownGrouping : dimension;
        const currentGroupingColumnBackendKey = dimensionMap[currentGroupingDimension]?.alias || currentGroupingDimension;

        let newColumnOrder: string[] = [];
        let newVisibleColumns: Record<string, boolean> = {};
        let newColumnWidths: Record<string, number> = savedWidths ? JSON.parse(savedWidths) : {};

        if (savedOrder) {
            const parsedOrder: string[] = JSON.parse(savedOrder);
            const currentHeadersSet = new Set(backendHeaders);
            const syncedOrder = parsedOrder.filter((col: string) => currentHeadersSet.has(col));
            newColumnOrder = syncedOrder;
            backendHeaders.forEach(header => {
                if (!newColumnOrder.includes(header)) {
                    newColumnOrder.push(header);
                }
            });
        } else {
            newColumnOrder = [...backendHeaders];
        }

        const fixedColumnsBackendKeys: string[] = [];
        const currentDimConfig = dimensionMap[currentGroupingDimension];

        // --- CAMBIO AÑADIDO AQUÍ ---
        if (currentGroupingDimension === 'campaign') {
            // Orden: 1. ID
            if (newColumnOrder.includes('campaign_id')) {
                newColumnOrder = newColumnOrder.filter(col => col !== 'campaign_id');
                fixedColumnsBackendKeys.push('campaign_id');
            }
            // Orden: 2. Fuente de Tráfico
            if (dimensionMap['traffic_source']?.alias && newColumnOrder.includes(dimensionMap['traffic_source'].alias)) {
                newColumnOrder = newColumnOrder.filter(col => col !== dimensionMap['traffic_source'].alias);
                fixedColumnsBackendKeys.push(dimensionMap['traffic_source'].alias);
            }
            // Orden: 3. Campaña
            if (currentDimConfig?.alias && newColumnOrder.includes(currentDimConfig.alias)) {
                newColumnOrder = newColumnOrder.filter(col => col !== currentDimConfig.alias);
                fixedColumnsBackendKeys.push(currentDimConfig.alias);
            }
        } else if (currentDimConfig) {
            if (currentDimConfig.idKey && newColumnOrder.includes(currentDimConfig.idKey)) {
                newColumnOrder = newColumnOrder.filter(col => col !== currentDimConfig.idKey);
                fixedColumnsBackendKeys.push(currentDimConfig.idKey);
            }
            if (currentDimConfig.alias && newColumnOrder.includes(currentDimConfig.alias)) {
                newColumnOrder = newColumnOrder.filter(col => col !== currentDimConfig.alias);
                fixedColumnsBackendKeys.push(currentDimConfig.alias);
            } else if (newColumnOrder.includes(currentGroupingDimension)) {
                newColumnOrder = newColumnOrder.filter(col => col !== currentGroupingDimension);
                fixedColumnsBackendKeys.push(currentGroupingDimension);
            }
        }

        newColumnOrder.unshift(...fixedColumnsBackendKeys);

        if (savedVisibility) {
            const parsedVisibility = JSON.parse(savedVisibility);
            newVisibleColumns = parsedVisibility;
            backendHeaders.forEach(header => {
                if (newVisibleColumns[header] === undefined) {
                    newVisibleColumns[header] = true;
                }
            });
        } else {
            const isDefaultView = (drillDownPath.length === 0 && dimension === 'campaign');
            newVisibleColumns = backendHeaders.reduce((acc, h) => {
                if (isDefaultView && h === 'traffic_source_id') {
                    acc[h] = false; // Ocultar por defecto
                } else {
                    acc[h] = true; // Mostrar el resto
                }
                return acc;
            }, {} as Record<string, boolean>);
        }

        fixedColumnsBackendKeys.forEach(col => {
            newVisibleColumns[col] = true;
        });

        setColumnOrder(newColumnOrder);
        setVisibleColumns(newVisibleColumns);
        setColumnWidths(newColumnWidths);

    }, [JSON.stringify(reportData), dimension, activeDrilldownGrouping, drillDownPath.length]);


    useEffect(() => {
        const savedOrder = localStorage.getItem('reportCardOrder');
        const savedVisibility = localStorage.getItem('reportCardVisibility');

        const defaultOrder = allMetricCards.map(c => c.key);
        const defaultVisibility = allMetricCards.reduce((acc, card) => ({...acc, [card.key]: true}), {});

        setVisibleCards(savedVisibility ? JSON.parse(savedVisibility) : defaultVisibility);
        setCardOrder(savedOrder ? JSON.parse(savedOrder) : defaultOrder);
    }, []);

    // processedData ahora no tiene ninguna lógica de filtrado por `selectedRows` directamente.
    // Solo se encarga de la búsqueda local (`searchTerm`) y los filtros avanzados numéricos.
    const processedData = useMemo(() => {
        let data = [...reportData];

        if (advancedFilters.length > 0) {
            data = data.filter(row => {
                return advancedFilters.every(filter => {
                    if (!filter.column || !filter.operator || filter.value === '') return true;
                    const rowValue = parseFloat(row[filter.column]);
                    const filterValue = parseFloat(filter.value);
                    if (isNaN(rowValue) || isNaN(filterValue)) return false;
                    switch (filter.operator) {
                        case '==': return rowValue === filterValue;
                        case '!=': return rowValue !== filterValue;
                        case '>': return rowValue > filterValue;
                        case '>=': return rowValue >= filterValue;
                        case '<': return rowValue < filterValue;
                        case '<=': return rowValue <= filterValue;
                        default: return true;
                    }
                });
            });
        }

        if (searchTerm) {
            data = data.filter(row => Object.values(row).some(value => {
                if (row['campaign_id'] && String(row['campaign_id']).toLowerCase().includes(searchTerm.toLowerCase())) {
                    return true;
                }
                return String(value).toLowerCase().includes(searchTerm.toLowerCase());
            }));
        }

        if (sortConfig) {
            data.sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];
                if (!isNaN(parseFloat(aVal)) && !isNaN(parseFloat(bVal))) {
                    return sortConfig.direction === 'ascending' ? parseFloat(aVal) - parseFloat(bVal) : parseFloat(bVal) - parseFloat(aVal);
                }
                return String(aVal).localeCompare(String(bVal)) * (sortConfig.direction === 'ascending' ? 1 : -1);
            });
        }

        return data;
    }, [reportData, searchTerm, sortConfig, advancedFilters]); // selectedRows ha sido eliminado de las dependencias.

    const paginatedData = useMemo(() => processedData.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE), [processedData, currentPage]);
    const totalPages = Math.ceil(processedData.length / ROWS_PER_PAGE);

    // --- Funciones de selección de filas ---
    const handleSelectAllRows = (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentPrimaryDimension = drillDownPath.length > 0 ? drillDownPath[drillDownPath.length - 1].dimension : dimension;
        // Solo permitir "seleccionar todo" si la agrupación es por campaña Y NO estamos en un drilldown.
        if (currentPrimaryDimension !== 'campaign' || drillDownPath.length > 0) return;

        if (event.target.checked) {
            const newSelected = new Set(selectedRows);
            paginatedData.forEach(row => {
                if (row.campaign_id) {
                    newSelected.add(String(row.campaign_id));
                }
            });
            setSelectedRows(newSelected);
        } else {
            const newSelected = new Set(selectedRows);
            // Solo deselecciona las filas que están en la página actual
            paginatedData.forEach(row => {
                if (row.campaign_id) {
                    newSelected.delete(String(row.campaign_id));
                }
            });
            setSelectedRows(newSelected);
        }
    };

    const handleSelectRow = (event: React.ChangeEvent<HTMLInputElement>, campaignId: string) => {
        event.stopPropagation();
        const newSelected = new Set(selectedRows);
        if (event.target.checked) {
            newSelected.add(campaignId);
        } else {
            newSelected.delete(campaignId);
        }
        setSelectedRows(newSelected);
    };

    const isRowSelected = (campaignId: string) => selectedRows.has(campaignId);

    const numSelected = selectedRows.size;
    // Indeterminate y AllSelected deben basarse en las filas VISIBLES EN PANTALLA que cumplen el criterio de campaña seleccionable
    const visibleCampaignRows = paginatedData.filter(row => (dimension === 'campaign' || drillDownPath.some(p => p.dimension === 'campaign')) && row.campaign_id);
    const isAllSelected = visibleCampaignRows.length > 0 && visibleCampaignRows.every(row => row.campaign_id && selectedRows.has(String(row.campaign_id)));
    const isIndeterminate = numSelected > 0 && numSelected < visibleCampaignRows.length;


    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, columnDisplayName: string) => {
        event.stopPropagation();
        const backendKey = getBackendKeyFromDisplayName(columnDisplayName, reportData);
        if (backendKey) {
            setAnchorEl(event.currentTarget);
            setSelectedColumn(backendKey);
        }
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCopyToClipboard = (mode: 'visible' | 'all', separator: string) => {
        if (!selectedColumn) return;
        const dataToCopy = mode === 'visible' ? paginatedData : processedData;
        const columnData = dataToCopy.map(row => row[selectedColumn]).join(separator);
        navigator.clipboard.writeText(columnData).then(() => {
            const displayName = getDisplayNameFromBackendKey(selectedColumn);
            setSnackbarMessage(`Copiados ${dataToCopy.length} valores de '${displayName}'`);
            setSnackbarOpen(true);
        }).catch(err => {
            setSnackbarMessage(`Error al copiar: ${err.message}`);
            setSnackbarOpen(true);
        });
        handleMenuClose();
    };


    const handleOpenColumnManager = () => setIsColumnManagerOpen(true);
    const handleCloseColumnManager = () => {
        setIsColumnManagerOpen(false);
        localStorage.setItem('reportColumnOrder', JSON.stringify(columnOrder));
        localStorage.setItem('reportColumnVisibility', JSON.stringify(visibleColumns));
        localStorage.setItem('reportColumnWidths', JSON.stringify(columnWidths));
    };
    const moveColumn = (index: number, direction: 'up' | 'down') => {
        const newOrder = [...columnOrder];
        const currentGroupingDimension = drillDownPath.length > 0 ? activeDrilldownGrouping : dimension;

        let fixedColumnCount = 0;
        if (currentGroupingDimension === 'campaign') {
            // Se usa .length para determinar el número de columnas fijas dinámicamente
            fixedColumnCount = ['campaign_id', dimensionMap['traffic_source']?.alias, dimensionMap['campaign']?.alias].filter(Boolean).length;
        } else {
            const currentDimConfig = dimensionMap[currentGroupingDimension];
            if (currentDimConfig?.idKey && newOrder.indexOf(currentDimConfig.idKey) === 0) {
                fixedColumnCount = 1;
                if (currentDimConfig.alias && newOrder.indexOf(currentDimConfig.alias) === 1) {
                    fixedColumnCount = 2;
                }
            } else if (currentDimConfig?.alias && newOrder.indexOf(currentDimConfig.alias) === 0) {
                fixedColumnCount = 1;
            } else if (newOrder.indexOf(currentGroupingDimension) === 0) {
                fixedColumnCount = 1;
            }
        }

        if (index < fixedColumnCount || (direction === 'up' && index - 1 < fixedColumnCount)) {
            return;
        }

        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex >= 0 && targetIndex < newOrder.length) {
            [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
            setColumnOrder(newOrder);
        }
    };

    const handleColumnVisibilityChange = (key: string, isVisible: boolean) => {
        const currentGroupingDimension = drillDownPath.length > 0 ? activeDrilldownGrouping : dimension;
        const currentDimConfig = dimensionMap[currentGroupingDimension];

        let isFixedColumn = false;
        if (currentGroupingDimension === 'campaign') {
            isFixedColumn = ['campaign_id', dimensionMap['traffic_source']?.alias, dimensionMap['campaign']?.alias].includes(key);
        } else if (currentDimConfig) {
            isFixedColumn = (key === currentDimConfig.idKey || key === currentDimConfig.alias || key === currentGroupingDimension);
        }

        if (isFixedColumn && !isVisible) {
            return;
        }
        setVisibleColumns(prev => ({ ...prev, [key]: isVisible }));
    };

    const handleOpenCardManager = () => setIsCardManagerOpen(true);
    const handleCloseCardManager = () => {
        setIsCardManagerOpen(false);
        localStorage.setItem('reportCardOrder', JSON.stringify(cardOrder));
        localStorage.setItem('reportCardVisibility', JSON.stringify(visibleCards));
    };
    const handleCardVisibilityChange = (key: string, isVisible: boolean) => {
        const newVisibility = { ...visibleCards, [key]: isVisible };
        setVisibleCards(newVisibility);
        const newOrder = allMetricCards.filter(c => newVisibility[c.key]).map(c => c.key);
        setCardOrder(newOrder);
    };
    const moveCard = (index: number, direction: 'up' | 'down') => {
        const newOrder = [...cardOrder];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex >= 0 && targetIndex < newOrder.length) {
            [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
            setCardOrder(newOrder);
        }
    };

    const requestSort = (columnDisplayName: string) => {
        const backendKey = getBackendKeyFromDisplayName(columnDisplayName, reportData);
        if (!backendKey) return;

        let direction: 'ascending' | 'descending' = 'ascending';
        if (sortConfig && sortConfig.key === backendKey && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key: backendKey, direction });
        setCurrentPage(1);
    };
    const handleFormSubmit = (e: React.FormEvent) => { e.preventDefault(); setCurrentPage(1); fetchData(); };
    const handleDrillDown = (row: ReportRow) => {
        const currentDimensionKey = drillDownPath.length > 0 ? activeDrilldownGrouping : dimension;
        const id = row[dimensionMap[currentDimensionKey]?.idKey || currentDimensionKey];

        let label: string;
        const currentDimensionConfig = dimensionMap[currentDimensionKey];
        if (currentDimensionConfig && ['campaign', 'offer', 'traffic_source', 'landing_page'].includes(currentDimensionKey)) {
            label = row[currentDimensionConfig.alias] || String(id);
        } else {
            label = String(row[currentDimensionKey] || id);
        }

        if (id === undefined) { setError(`Error de Drilldown: No se encontró la columna ID para '${currentDimensionKey}'.`); return; }

        const currentDimensionIndex = allDimensions.findIndex(d => d.id === currentDimensionKey);
        let nextGroupingDimension = '';
        if (currentDimensionIndex !== -1 && currentDimensionIndex + 1 < allDimensions.length) {
            nextGroupingDimension = allDimensions[currentDimensionIndex + 1].id;
        } else {
            nextGroupingDimension = 'country_code';
        }
        setActiveDrilldownGrouping(nextGroupingDimension);

        setDrillDownPath(prev => [...prev, { dimension: currentDimensionKey, value: id, label: String(label) }]);
        setSelectedRows(new Set()); // Limpiar selección al hacer drilldown
        setCurrentPage(1);
        setSearchTerm('');
    };
    const handleBreadcrumbClick = (index: number) => {
        const newPath = drillDownPath.slice(0, index);
        setDrillDownPath(newPath);
        setCurrentPage(1);
        setSearchTerm('');
        setSelectedRows(new Set()); // Limpiar selección al navegar por breadcrumbs
        if (index > 0) {
            setActiveDrilldownGrouping(newPath[newPath.length - 1].dimension);
        } else {
            setActiveDrilldownGrouping('');
            setDimension('campaign');
        }
    };
    const handlePresetDateRange = (range: keyof typeof datePresetTranslations) => { const today = new Date(); today.setHours(0, 0, 0, 0); let newStartDate = new Date(today); let newEndDate = new Date(today); switch (range) { case 'today': break; case 'yesterday': newStartDate.setDate(today.getDate() - 1); newEndDate.setDate(today.getDate() - 1); break; case 'last_7_days': newStartDate.setDate(today.getDate() - 6); break; case 'last_30_days': newStartDate.setDate(today.getDate() - 29); break; case 'this_month': newStartDate = new Date(today.getFullYear(), today.getMonth(), 1); break; case 'last_month': newEndDate = new Date(today.getFullYear(), today.getMonth(), 0); newStartDate = new Date(newEndDate.getFullYear(), newEndDate.getMonth(), 1); break; case 'all_time': newStartDate = new Date('2020-01-01'); break; } const formatDate = (date: Date) => date.toISOString().split('T')[0]; setStartDate(formatDate(newStartDate)); setEndDate(formatDate(newEndDate)); };

    const addAdvancedFilter = () => {
        setAdvancedFilters(prev => [...prev, { id: Date.now(), column: 'total_clicks', operator: '>=', value: '' }]);
        setCurrentPage(1);
    };

    const removeAdvancedFilter = (id: number) => {
        setAdvancedFilters(prev => prev.filter(f => f.id !== id));
        setCurrentPage(1);
    };

    const updateAdvancedFilter = (id: number, field: 'column' | 'operator' | 'value', value: string) => {
        setAdvancedFilters(prev => prev.map(f => f.id === id ? { ...f, [field]: value } : f));
        setCurrentPage(1);
    };

    const SortIcon = ({ columnKey }: { columnKey: string }) => {
        const backendKey = getBackendKeyFromDisplayName(columnKey, reportData);
        if (!sortConfig || sortConfig.key !== backendKey) {
            return <UnfoldMoreIcon sx={{ fontSize: 16, verticalAlign: 'middle', ml: 0.5, opacity: 0.3 }} />;
        }
        return sortConfig.direction === 'ascending' ? <ArrowUpwardIcon sx={{ fontSize: 16, verticalAlign: 'middle', ml: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16, verticalAlign: 'middle', ml: 0.5 }} />;
    };

    const getProfitColor = (profit: number) => profit > 0 ? theme.palette.success.main : profit < 0 ? theme.palette.error.main : theme.palette.text.secondary;

    const renderGroupedMenuItems = (dims: typeof allDimensions) => {
        const grouped = dims.reduce((acc, dim) => {
            acc[dim.group] = [...(acc[dim.group] || []), dim];
            return acc;
        }, {} as Record<string, typeof allDimensions>);

        return Object.entries(grouped).flatMap(([group, groupDims]) => [
            <ListSubheader key={group}>{group}</ListSubheader>,
            ...groupDims.map(d => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)
        ]);
    };

    const headerCellStyle = { backgroundColor: alpha(theme.palette.primary.main, 0.1), borderBottom: `2px solid ${theme.palette.primary.main}`, whiteSpace: 'nowrap' };

    const startResizing = useCallback((e: React.MouseEvent<HTMLDivElement>, columnBackendKey: string) => {
        e.stopPropagation();
        setResizingColumn(columnBackendKey);
        setStartX(e.clientX);
        const header = (e.target as HTMLElement).closest('th');
        if (header) {
            setStartWidth(header.offsetWidth);
        }
    }, []);

    const resizeColumn = useCallback((e: MouseEvent) => {
        if (resizingColumn) {
            const newWidth = startWidth + (e.clientX - startX);
            setColumnWidths(prev => ({
                ...prev,
                [resizingColumn]: Math.max(50, newWidth)
            }));
        }
    }, [resizingColumn, startX, startWidth]);

    const stopResizing = useCallback(() => {
        setResizingColumn(null);
        localStorage.setItem('reportColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        if (resizingColumn) {
            window.addEventListener('mousemove', resizeColumn);
            window.addEventListener('mouseup', stopResizing);
        } else {
            window.removeEventListener('mousemove', resizeColumn);
            window.removeEventListener('mouseup', stopResizing);
        }
        return () => {
            window.removeEventListener('mousemove', resizeColumn);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [resizingColumn, resizeColumn, stopResizing]);


    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <InsightsIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Explorador de Reportes
            </Typography>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <ListSubheader>Copiar Visibles ({paginatedData.length} filas)</ListSubheader>
                {separators.map(sep => (
                    <MenuItem key={`vis-${sep.key}`} onClick={() => handleCopyToClipboard('visible', sep.value)}>
                        {sep.label}
                    </MenuItem>
                ))}
                <Divider />
                <ListSubheader>Copiar Todo ({processedData.length} filas)</ListSubheader>
                {separators.map(sep => (
                    <MenuItem key={`all-${sep.key}`} onClick={() => handleCopyToClipboard('all', sep.value)}>
                        {sep.label}
                    </MenuItem>
                ))}
            </Menu>

            <Dialog open={isColumnManagerOpen} onClose={handleCloseColumnManager} fullWidth maxWidth="xs">
                <DialogTitle>Gestionar Columnas</DialogTitle>
                <DialogContent>
                    <List>
                        {columnOrder.map((backendKey, index) => {
                            const displayName = getDisplayNameFromBackendKey(backendKey);
                            const currentGroupingDimension = drillDownPath.length > 0 ? activeDrilldownGrouping : dimension;
                            const currentDimConfig = dimensionMap[currentGroupingDimension];

                            let isFixedColumn = false;
                            if (currentGroupingDimension === 'campaign') {
                                isFixedColumn = ['campaign_id', dimensionMap['traffic_source']?.alias, dimensionMap['campaign']?.alias].includes(backendKey);
                            } else if (currentDimConfig) {
                                isFixedColumn = (backendKey === currentDimConfig.idKey || backendKey === currentDimConfig.alias || backendKey === currentGroupingDimension);
                            }

                            return (
                                <ListItem key={backendKey} secondaryAction={
                                    <>
                                        <IconButton edge="end" aria-label="up" onClick={() => moveColumn(index, 'up')} disabled={isFixedColumn || index === 0}>
                                            <ArrowUpwardIcon />
                                        </IconButton>
                                        <IconButton edge="end" aria-label="down" onClick={() => moveColumn(index, 'down')} disabled={isFixedColumn || index === columnOrder.length - 1}>
                                            <ArrowDownwardIcon />
                                        </IconButton>
                                    </>
                                }>
                                    <ListItemIcon>
                                        <Switch
                                            edge="start"
                                            checked={!!visibleColumns[backendKey]}
                                            onChange={(e) => handleColumnVisibilityChange(backendKey, e.target.checked)}
                                            disabled={isFixedColumn}
                                        />
                                    </ListItemIcon>
                                    <ListItemText primary={displayName} />
                                </ListItem>
                            );
                        })}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseColumnManager}>Guardar y Cerrar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isCardManagerOpen} onClose={handleCloseCardManager} fullWidth maxWidth="sm">
                <DialogTitle>Gestionar Tarjetas de Métricas</DialogTitle>
                <DialogContent>
                    <List>
                        {allMetricCards.map((card) => (
                            <ListItem key={card.key}>
                                <ListItemIcon>
                                    <Switch
                                        edge="start"
                                        checked={!!visibleCards[card.key]}
                                        onChange={(e) => handleCardVisibilityChange(card.key, e.target.checked)}
                                    />
                                </ListItemIcon>
                                <ListItemText primary={card.label} />
                            </ListItem>
                        ))}
                    </List>
                    <Divider sx={{ my: 2 }} />
                    <Typography>Reordenar Tarjetas Visibles</Typography>
                    <List>
                        {cardOrder.map((key, index) => (
                            <ListItem key={key} secondaryAction={
                                <>
                                    <IconButton edge="end" aria-label="up" onClick={() => moveCard(index, 'up')} disabled={index === 0}>
                                        <ArrowUpwardIcon />
                                    </IconButton>
                                    <IconButton edge="end" aria-label="down" onClick={() => moveCard(index, 'down')} disabled={index === cardOrder.length - 1}>
                                        <ArrowDownwardIcon />
                                    </IconButton>
                                </>
                            }>
                                <ListItemText primary={allMetricCards.find(c => c.key === key)?.label} />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCardManager}>Guardar y Cerrar</Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 3, alignItems: 'flex-start' }}>
                <Box sx={{ position: { md: 'sticky' }, top: '20px' }}>
                    <Paper elevation={3} sx={{ p: 2, borderRadius: '12px' }}>
                        <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>Filtros y Agrupación</Typography>
                        <Box component="form" onSubmit={handleFormSubmit} noValidate>
                            <Stack spacing={2.5}>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {Object.keys(datePresetTranslations).map(range => (
                                        <Chip key={range} label={datePresetTranslations[range]} onClick={() => handlePresetDateRange(range as keyof typeof datePresetTranslations)} clickable size="small" />
                                    ))}
                                </Stack>
                                <TextField fullWidth label="Desde" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small"/>
                                <TextField fullWidth label="Hasta" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small"/>

                                <FormControl fullWidth size="small" disabled={drillDownPath.length > 0}>
                                    <InputLabel>Agrupación Inicial</InputLabel>
                                    <Select label="Agrupación Inicial" value={dimension} onChange={(e: SelectChangeEvent) => setDimension(e.target.value)}>
                                        {renderGroupedMenuItems(allDimensions)}
                                    </Select>
                                </FormControl>
                                <Button type="submit" variant="contained" disabled={isLoading} size="large">{isLoading ? 'Cargando...' : 'Generar Reporte'}</Button>
                            </Stack>
                        </Box>

                        <Divider sx={{ my: 2.5 }} />

                        <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>Filtros Avanzados</Typography>
                        <Stack spacing={2}>
                            {advancedFilters.map((filter) => (
                                <Stack key={filter.id} direction="row" spacing={1} alignItems="center">
                                    <FormControl size="small" sx={{ flex: 3 }}>
                                        <InputLabel>Métrica</InputLabel>
                                        <Select
                                            value={filter.column}
                                            label="Métrica"
                                            onChange={(e) => updateAdvancedFilter(filter.id, 'column', e.target.value)}
                                        >
                                            {allMetricCards.map(m => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ flex: 1.5 }}>
                                        <InputLabel>Op</InputLabel>
                                        <Select
                                            value={filter.operator}
                                            label="Op"
                                            onChange={(e) => updateAdvancedFilter(filter.id, 'operator', e.target.value)}
                                        >
                                            {filterOperators.map(op => <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        label="Valor"
                                        type="number"
                                        value={filter.value}
                                        onChange={(e) => updateAdvancedFilter(filter.id, 'value', e.target.value)}
                                        sx={{ flex: 2 }}
                                    />
                                    <IconButton onClick={() => removeAdvancedFilter(filter.id)} size="small">
                                        <DeleteIcon />
                                    </IconButton>
                                </Stack>
                            ))}
                            <Button
                                startIcon={<AddCircleOutlineIcon />}
                                onClick={addAdvancedFilter}
                                size="small"
                                sx={{ justifyContent: 'flex-start' }}
                            >
                                Añadir Filtro
                            </Button>
                        </Stack>
                    </Paper>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Stack spacing={3}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 2 }}>
                            {cardOrder.map(key => visibleCards[key] && (
                                <Card key={key} elevation={2} sx={{ borderRadius: '12px', textAlign: 'center' }}>
                                    <CardContent>
                                        <Typography variant="caption" color="text.secondary">{allMetricCards.find(c => c.key === key)?.label.toUpperCase()}</Typography>
                                        {isLoading && !totals ? <Skeleton variant="text" width="80%" sx={{mx: 'auto'}}/> :
                                        <Typography variant="h5" sx={{fontWeight: 'bold', color: key === 'profit' ? getProfitColor(parseFloat(totals?.[key] || '0')) : 'text.primary'}}>
                                            {['total_clicks', 'lp_clicks', 'total_conversions'].includes(key) ? formatNumber(parseFloat(totals?.[key] || '0'))
                                                // MODIFICACIÓN: Se ajustan los decimales para EPC/CPC a min 3, max 5.
                                                : ['epc', 'cpc'].includes(key) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 3, maximumFractionDigits: 5 }).format(parseFloat(totals?.[key] || '0'))
                                                // Formato para eCPA se mantiene con 2 decimales
                                                : ['ecpa'].includes(key) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(totals?.[key] || '0'))
                                                // Formato para Costo, Payout y Profit con 2 decimales
                                                : ['total_cost', 'total_payout', 'profit'].includes(key) ? formatCurrency(parseFloat(totals?.[key] || '0'))
                                                : ['lp_ctr', 'roi', 'cvr'].includes(key) ? `${parseFloat(totals?.[key] || '0').toFixed(2)}%`
                                                : formatNumber(parseFloat(totals?.[key] || '0'))}
                                        </Typography>
                                        }
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>

                        <Paper elevation={3} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                           <Stack direction={{xs: 'column', md: 'row'}} justifyContent="space-between" alignItems="center" spacing={1} sx={{ p: 2, flexWrap: 'wrap', borderBottom: `1px solid ${theme.palette.divider}` }}>
                                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                                    <MuiLink component="button" onClick={() => handleBreadcrumbClick(0)} sx={{cursor: 'pointer'}} color="inherit">Reporte General</MuiLink>
                                    {drillDownPath.map((item, i) =>
                                        i === drillDownPath.length - 1 ? (
                                            <Typography key={i} color="text.primary">{item.label}</Typography>
                                        ) : (
                                            <MuiLink component="button" key={i} onClick={() => handleBreadcrumbClick(i + 1)} sx={{cursor: 'pointer'}} color="inherit">{item.label}</MuiLink>
                                        )
                                    )}
                                </Breadcrumbs>

                                {drillDownPath.length > 0 && (
                                    <FormControl size="small" sx={{ minWidth: 220 }}>
                                        <InputLabel>Agrupar Por</InputLabel>
                                        <Select label="Agrupar Por" value={activeDrilldownGrouping} onChange={(e: SelectChangeEvent) => setActiveDrilldownGrouping(e.target.value)}>
                                            {renderGroupedMenuItems(availableDrilldownDimensions)}
                                        </Select>
                                    </FormControl>
                                )}

                                <Tooltip title="Gestionar Tarjetas"><IconButton onClick={handleOpenCardManager}><DashboardCustomizeIcon /></IconButton></Tooltip>
                                <Tooltip title="Gestionar Columnas"><IconButton onClick={handleOpenColumnManager}><ViewColumnIcon /></IconButton></Tooltip>
                                <Tooltip title="Refrescar datos actuales"><span><IconButton onClick={fetchData} disabled={isLoading}><RefreshIcon /></IconButton></span></Tooltip>
                                {/* Nuevo botón para el reporte agrupado */}
                                {/* Este botón ahora prepara un "drilldown agrupado" */}
                                {(dimension === 'campaign' && drillDownPath.length === 0) && (
                                    <Tooltip title="Ver reporte agrupado de las campañas seleccionadas">
                                        <span>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => {
                                                    // 1. Crear el entry especial para el drilldown agrupado
                                                    const groupedCampaignIds = Array.from(selectedRows).join(',');
                                                    const groupedDrilldownEntry = {
                                                        dimension: 'campaign', // La dimensión base que estamos agrupando/filtrando
                                                        value: groupedCampaignIds, // Los IDs (comma-separated)
                                                        label: `Campañas Seleccionadas (${selectedRows.size})`, // Etiqueta para el breadcrumb
                                                    };
                                                    
                                                    // 2. Determinar la siguiente dimensión de agrupación real para el reporte
                                                    const currentDimensionIndex = allDimensions.findIndex(d => d.id === dimension); // Debería ser 'campaign'
                                                    let nextGroupingDim = 'offer'; // Default fallback
                                                    if (currentDimensionIndex !== -1 && currentDimensionIndex + 1 < allDimensions.length) {
                                                        nextGroupingDim = allDimensions[currentDimensionIndex + 1].id;
                                                    }


                                                    // 3. Actualizar los estados para reflejar este "grouped drilldown"
                                                    setDrillDownPath([groupedDrilldownEntry]); // El path comienza con la entrada agrupada
                                                    setActiveDrilldownGrouping(nextGroupingDim); // Establece la próxima agrupación para la API
                                                    setDimension('campaign'); // Asegurarse que la dimensión principal sigue siendo campaña para la UI
                                                    setSelectedRows(new Set()); // Limpiar las selecciones después de generar el reporte
                                                    setCurrentPage(1); // Resetear paginación
                                                    setSearchTerm(''); // Limpiar búsqueda

                                                    // fetchData se ejecutará automáticamente debido a los cambios en drillDownPath y activeDrilldownGrouping
                                                }}
                                                disabled={selectedRows.size === 0 || isLoading}
                                                sx={{ ml: 1 }}
                                            >
                                                Ver Agrupado ({selectedRows.size})
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                           </Stack>

                           <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                <TextField fullWidth variant="outlined" size="small" placeholder="Buscar en los resultados..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} InputProps={{ startAdornment: (<SearchIcon fontSize="small" sx={{mr:1}}/>) }}/>
                           </Box>

                            <TableContainer sx={{ maxHeight: 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            {/* Nueva columna de selección */}
                                            <TableCell padding="checkbox" sx={{ ...headerCellStyle, width: '40px', minWidth: '40px' }}>
                                                {(dimension === 'campaign' && drillDownPath.length === 0) && ( // Solo el checkbox de "seleccionar todo" si estamos en el nivel de campañas principal
                                                    <Checkbox
                                                        color="primary"
                                                        indeterminate={isIndeterminate}
                                                        checked={paginatedData.length > 0 && isAllSelected}
                                                        onChange={handleSelectAllRows}
                                                        inputProps={{
                                                            'aria-label': 'select all campaigns',
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            {columnOrder.filter(backendKey => visibleColumns[backendKey]).map((backendKey, index) => {
                                                const displayName = getDisplayNameFromBackendKey(backendKey);
                                                // La columna es drillable si es la primera columna de datos visible
                                                // Y NO estamos en modo de selección de campañas agrupadas (es decir, selectedRows está vacío)
                                                const isDrillable = index === 0 && selectedRows.size === 0;

                                                return (
                                                    <TableCell
                                                        key={backendKey}
                                                        sx={{
                                                            ...headerCellStyle,
                                                            p: '6px 8px',
                                                            width: columnWidths[backendKey] || 'auto',
                                                            minWidth: '50px',
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
                                                            <Box onClick={() => requestSort(displayName)} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexGrow: 1 }}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                                                    {displayName}
                                                                </Typography>
                                                                <SortIcon columnKey={displayName} />
                                                            </Box>
                                                            <Tooltip title="Opciones de Copia">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => handleMenuOpen(e, displayName)}
                                                                    sx={{ p: '2px' }}
                                                                >
                                                                    <MoreVertIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                        <Box
                                                            onMouseDown={(e) => startResizing(e, backendKey)}
                                                            sx={{
                                                                position: 'absolute',
                                                                right: 0,
                                                                top: 0,
                                                                width: '5px',
                                                                cursor: 'col-resize',
                                                                height: '100%',
                                                                zIndex: 1,
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.primary.light,
                                                                },
                                                            }}
                                                        />
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isLoading ? Array.from(new Array(5)).map((_, i) => (
                                            <TableRow key={i}>
                                                <TableCell padding="checkbox"><Skeleton /></TableCell>
                                                {columnOrder.filter(h => visibleColumns[h]).map(h => <TableCell key={h}><Skeleton /></TableCell>)}
                                            </TableRow>
                                        )) :
                                        paginatedData.map((row, rowIndex) => (
                                            <TableRow key={rowIndex} hover>
                                                <TableCell padding="checkbox">
                                                    {(dimension === 'campaign' || drillDownPath.some(p => p.dimension === 'campaign')) && row.campaign_id && (
                                                        <Checkbox
                                                            color="primary"
                                                            checked={isRowSelected(String(row.campaign_id))}
                                                            onChange={(event) => handleSelectRow(event, String(row.campaign_id))}
                                                            inputProps={{ 'aria-labelledby': `campaign-row-${row.campaign_id}` }}
                                                        />
                                                    )}
                                                </TableCell>
                                                {columnOrder.filter(backendKey => visibleColumns[backendKey]).map((backendKey, index) => {
                                                    const displayName = getDisplayNameFromBackendKey(backendKey);
                                                    const isMetric = allMetricCards.some(m => m.key === backendKey);
                                                    const cellValue = row[backendKey];
                                                    // La columna es drillable si es la primera columna de datos visible Y no hay campañas seleccionadas
                                                    const isDrillable = index === 0 && selectedRows.size === 0;

                                                    return (
                                                        <TableCell
                                                            key={`${backendKey}-${rowIndex}`}
                                                            sx={{
                                                                whiteSpace: 'nowrap',
                                                                cursor: 'default',
                                                                ...getMetricStyle(displayName, cellValue, theme)
                                                            }}
                                                        >
                                                            {isDrillable ? (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                    <Typography component="span" sx={{ flexGrow: 1 }}>
                                                                        {isMetric ? formatMetricCell(displayName, cellValue) : formatDimensionCell(row, backendKey)}
                                                                    </Typography>
                                                                    <Tooltip title={`Drill down por ${displayName}`}>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => { e.stopPropagation(); handleDrillDown(row); }}
                                                                            sx={{ ml: 0.5 }}
                                                                        >
                                                                            <NavigateNextIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            ) : (
                                                                isMetric ? formatMetricCell(displayName, cellValue) : formatDimensionCell(row, backendKey)
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                             {paginatedData.length === 0 && !isLoading && <Typography sx={{ textAlign: 'center', p: 4 }} color="text.secondary">No hay datos que coincidan con tu selección.</Typography>}
                            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`}}>
                                <Button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Anterior</Button>
                                <Typography variant="body2" color="text.secondary">Página {currentPage} de {totalPages || 1}</Typography>
                                <Button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0}>Siguiente</Button>
                            </Stack>
                        </Paper>
                    </Stack>
                </Box>
            </Box>
        </Container>
    );
};

export default CampaignsReportPage;